import React, { useState, useContext, useEffect } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import Map from "../../components/Map";
import useThunk from "../../hooks/thunk";
import NavContext from "../../contexts/Nav";
import { AuthContext } from "../../contexts/Auth";
import AlarmsContext from "../../contexts/Alarms";
import { clearIntent } from "../../actions/Auth";
import { get } from "../../api";
import { fetchAlarms } from "../../actions/Alarms";
import { startPolling } from "../../helpers/polling";
import { AlarmsReducer, initialAlarmsState } from "../../reducers/Alarms";
import { SYS_ADMIN, TENANT_ADMIN, CUSTOMER_USER } from "../../constants/roles";
import AssetsFiltered from "../AssetsFiltered";
import Account from "../Account";
import Alarms from "../Alarms";
import Reports from "../Reports";
import AuthorizedRoute from "../../components/AuthorizedRoute";
import CustomersIndex from "../CustomersIndex";
import CustomerIndex from "../CustomerIndex";
import Manage from "../Manage";
import Navigation from "../../components/Navigation";
import TenantsIndex from "../TenantsIndex";
import UsersIndex from "../UsersIndex";
import Help from "../Help";
import styles from "./Sentinel.module.scss";
import MapDisplay from "../MapDisplay";
import TypesContext from "../../contexts/Types";
import DevicesSimple from "../DevicesSimple";
import AdminPortal from "../AdminPortal";
import MobileHistory from "../MobileHistory";
import TrailerHistory from "../TrailerHistory";
import NewPendingCustomer from "../NewPendingCustomer";
import Groups from "../Groups";
import CustomerUsersIndex from "../CustomerUsersIndex";
import SignupApproval from "../SignupApproval";
import CustomerSubscriptionDetailWrapper from "../CustomerSubscriptionDetailWrapper";
import { set } from "date-fns";

export default function Sentinel({ location }) {
  const { state, dispatch } = useContext(AuthContext);
  const [noSubscrition, setNoSubscription] = useState(false);
  const [alarmsState, alarmsDispatch] = useThunk(
    AlarmsReducer,
    initialAlarmsState
  );
  const [subcriptionAlert, setSubscriptionAlert] = useState(false);

  const checkSubscription = async () => {
    try {
      const customerPaymentProfile = await get(`/customers/paymentprofile`);

      /**
       * A request that failed resolves to `undefined`, which is not the server
       * saying the customer has no subscription. Treating the two the same
       * raised the no-subscription alert whenever the connection dropped, and
       * because `noSubscrition` is a dependency of the effect below that also
       * rebuilt the timer and added a permanent 30-second poll for this
       * endpoint. Leave the current state alone until an answer arrives.
       */
      if (customerPaymentProfile === undefined) return;

      const profile = customerPaymentProfile?.[0];

      if (!profile) {
        setNoSubscription(true);
        setSubscriptionAlert(true);
        return;
      }

      if (profile && profile?.payment) {
        setNoSubscription(false);
        setSubscriptionAlert(false);
      }
    } catch (error) {
      /** Also not evidence of a missing subscription — report, don't alert. */
      console.error("Error fetching customer payment profile:", error);
    }
  };

  async function fetchTypes() {
    //setLoading(true);

    const [assetTypes, deviceTypes] = await Promise.all([
      get("/assettypes"),
      get("/devicetypes"),
    ]);

    assetTypes?.results &&
      setTypes({
        assets: assetTypes.results,
        devices: deviceTypes?.results,
      });

    //setLoading(false);
  }
  const [isNavShowing, setIsNavShowing] = useState(false);
  const [currentPath, setCurrentPath] = useState(null);
  const [types, setTypes] = useState({});
  useEffect(() => {
    /** Check if there's any post-auth intent */
    if (state.intent) {
      dispatch(clearIntent());
    }
    checkSubscription();
    fetchTypes();
    /**
     * Check alarms now, then 30 seconds after each check completes. A fixed
     * setInterval kept firing while a slow /groupalarms call was still in
     * flight, so requests stacked up on the API and slowed it down further.
     */
    const stopPollingAlarms = startPolling(
      () => alarmsDispatch(fetchAlarms()),
      1000 * 30
    );
    /** While there is no subscription, keep looking for one */
    const subscriptionTimer = noSubscrition
      ? setInterval(checkSubscription, 1000 * 30)
      : null;

    return () => {
      stopPollingAlarms();
      clearInterval(subscriptionTimer);
    };
  }, [noSubscrition]);

  useEffect(() => {
    const routeChanged = currentPath !== location.pathname;

    if (routeChanged) {
      setCurrentPath(location.pathname);
      setIsNavShowing(false);
    }
  }, [location.pathname, currentPath]);

  const sentinelClasses = [
    styles.sentinel,
    isNavShowing ? styles["sentinel--nav-showing"] : "",
  ].join(" ");

  return state.intent ? (
    <Redirect to={state.intent} noThrow />
  ) : (
    <TypesContext.Provider value={types}>
      <AlarmsContext.Provider value={[alarmsState, alarmsDispatch]}>
        <NavContext.Provider value={{ isNavShowing, setIsNavShowing }}>
          <div className={sentinelClasses}>
            <div className={styles.nav}>
              <Navigation
                user={state.user}
                alarmCount={
                  alarmsState && alarmsState.alarms
                    ? alarmsState.alarms.length
                    : 0
                }
                subcriptionAlert={subcriptionAlert}
              />
            </div>

            <main className={styles.page}>
              <Switch>
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN, CUSTOMER_USER]}
                  path="/alarms"
                  component={Alarms}
                />
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN, CUSTOMER_USER]}
                  path="/reports"
                  component={Reports}
                />

                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN]}
                  path="/tenants"
                  component={TenantsIndex}
                />

                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN]}
                  path="/customers"
                  component={CustomersIndex}
                />

                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[CUSTOMER_USER]}
                  userPermissions={state.user.perms}
                  allowedPermissions={["ADMIN"]}
                  path="/customer/users"
                  component={CustomerUsersIndex}
                />
                {/* Approve link from the sign-up attempt email; ADMIN or USER_C */}
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN, CUSTOMER_USER]}
                  userPermissions={state.user.perms}
                  allowedPermissions="USER_C"
                  path="/signup-approval/:token"
                  component={SignupApproval}
                />

                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[CUSTOMER_USER]}
                  userPermissions={state.user.perms}
                  allowedPermissions={["ADMIN"]}
                  path="/customer"
                  component={CustomerIndex}
                />
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[CUSTOMER_USER]}
                  path="/groups"
                  component={Groups}
                />
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[CUSTOMER_USER]}
                  path="/subscription"
                  component={CustomerSubscriptionDetailWrapper}
                />

                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN]}
                  path="/users"
                  component={UsersIndex}
                />

                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN, CUSTOMER_USER]}
                  path="/assets_dashboard"
                  component={AssetsFiltered}
                />
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN]}
                  path="/admin_portal"
                  component={AdminPortal}
                />
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN]}
                  path="/pendingcustomer/:id(\d+)"
                  component={NewPendingCustomer}
                />
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[CUSTOMER_USER]}
                  path="/mobile_history"
                  component={MobileHistory}
                />
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[CUSTOMER_USER]}
                  path="/trailer_history"
                  component={TrailerHistory}
                />
                <Route path="/map" component={MapDisplay} />

                <Route path="/account" component={Account} />
                <Route path="/devices" component={DevicesSimple} />
                <Route path="/help" component={Help} />
                <AuthorizedRoute
                  userRoles={state.user.roles}
                  allowedRoles={[SYS_ADMIN, TENANT_ADMIN, CUSTOMER_USER]}
                  path="/"
                  component={Manage}
                />
              </Switch>
            </main>
          </div>
        </NavContext.Provider>
      </AlarmsContext.Provider>
    </TypesContext.Provider>
  );
}
